import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import NewsManager from '../components/admin/NewsManager';
import StatsManager from '../components/admin/StatsManager';
import UserManager from '../components/admin/UserManager';
import UserRoleManager from '../components/admin/UserRoleManager';
import SeasonManager from '../components/admin/SeasonManager';
import LogsManager from '../components/admin/LogsManager';
import WebhooksManager from '../components/admin/WebhooksManager';
import RulebookBlockBuilder from '../components/admin/RulebookBlockBuilder';
import StreamsManager from '../components/admin/StreamsManager';
import ChangePassword from '../components/admin/ChangePassword';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout, hasPermission } = useAdminAuth();
  const navigate = useNavigate();

  if (!user) {
    navigate('/admin/login');
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const canManageStats = hasPermission('manage_stats');
  const canEditStats = hasPermission('edit_stats');
  const canEditNews = hasPermission('edit_news');
  const canManageUsers = hasPermission('manage_users');
  const canManageSeasons = hasPermission('manage_permissions');
  const canManageWebhooks = hasPermission('manage_permissions');
  const canManageRulebook = hasPermission('manage_rulebook');

  return (
    <div className="admin-dashboard-container">
      <button 
        className={`sidebar-toggle ${sidebarCollapsed ? 'collapsed' : ''}`}
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
      >
        ☰
      </button>
      <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="admin-logo">
          <h2>HSFL Admin</h2>
        </div>

        <nav className="admin-nav">
          <button
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>

          {(canManageStats || canEditStats) && (
            <button
              className={`nav-item ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              Stats Manager
            </button>
          )}

          {canEditNews && (
            <button
              className={`nav-item ${activeTab === 'news' ? 'active' : ''}`}
              onClick={() => setActiveTab('news')}
            >
              News Manager
            </button>
          )}

          {canManageUsers && (
            <button
              className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              User Manager
            </button>
          )}

          {canManageUsers && (
            <button
              className={`nav-item ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
            >
              Assign Roles
            </button>
          )}

          {canManageSeasons && (
            <button
              className={`nav-item ${activeTab === 'seasons' ? 'active' : ''}`}
              onClick={() => setActiveTab('seasons')}
            >
              Season Management
            </button>
          )}

          <button
            className={`nav-item ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            Logs & Audit
          </button>

          {canManageWebhooks && (
            <button
              className={`nav-item ${activeTab === 'webhooks' ? 'active' : ''}`}
              onClick={() => setActiveTab('webhooks')}
            >
              Webhooks
            </button>
          )}

          {canManageRulebook && (
            <button
              className={`nav-item ${activeTab === 'rulebook' ? 'active' : ''}`}
              onClick={() => setActiveTab('rulebook')}
            >
              Rulebook
            </button>
          )}

          {(canManageStats || canEditStats) && (
            <button
              className={`nav-item ${activeTab === 'streams' ? 'active' : ''}`}
              onClick={() => setActiveTab('streams')}
            >
              Live Streams
            </button>
          )}
        </nav>

        <div className="admin-user-info">
          <div className="user-details">
            <p className="user-email">{user.email}</p>
            <p className="user-role">{user.role}</p>
          </div>
          <button onClick={() => setShowChangePassword(true)} className="change-password-button">
            Change Password
          </button>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </aside>

      <main className="admin-content">
        <div className="admin-header">
          <h1>
            {activeTab === 'dashboard' && 'Dashboard'}
            {activeTab === 'stats' && 'Statistics Manager'}
            {activeTab === 'news' && 'News Manager'}
            {activeTab === 'users' && 'User Manager'}
            {activeTab === 'roles' && 'Assign Roles'}
            {activeTab === 'seasons' && 'Season Management'}
            {activeTab === 'logs' && 'Logs & Audit Trail'}
            {activeTab === 'webhooks' && 'Webhook Manager'}
            {activeTab === 'rulebook' && 'Rulebook Manager'}
            {activeTab === 'streams' && 'Live Streams Manager'}
          </h1>
        </div>

        <div className="admin-panel">
          {activeTab === 'dashboard' && (
            <div className="dashboard-overview">
              <div className="welcome-card">
                <h2>Welcome, {user.email.split('@')[0]}!</h2>
                <p>You are logged in with role: <strong>{user.role}</strong></p>
                <p>Select a manager from the sidebar to get started.</p>
              </div>

              <div className="permissions-overview">
                <h3>Your Permissions</h3>
                <div className="permission-grid">
                  <button
                    className={`permission-item ${(canManageStats || canEditStats) ? 'active' : 'inactive'}`}
                    onClick={() => (canManageStats || canEditStats) && setActiveTab('stats')}
                  >
                    <span className="permission-icon">📊</span>
                    <span>Stats Manager</span>
                  </button>
                  <button
                    className={`permission-item ${canEditNews ? 'active' : 'inactive'}`}
                    onClick={() => canEditNews && setActiveTab('news')}
                  >
                    <span className="permission-icon">📰</span>
                    <span>Edit News</span>
                  </button>
                  <button
                    className={`permission-item ${canManageUsers ? 'active' : 'inactive'}`}
                    onClick={() => canManageUsers && setActiveTab('users')}
                  >
                    <span className="permission-icon">👥</span>
                    <span>Manage Users</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stats' && (canManageStats || canEditStats) && <StatsManager />}
          {activeTab === 'news' && canEditNews && <NewsManager />}
          {activeTab === 'users' && canManageUsers && <UserManager />}
          {activeTab === 'roles' && canManageUsers && <UserRoleManager />}

          {activeTab === 'seasons' && canManageSeasons && <SeasonManager />}

          {activeTab === 'logs' && <LogsManager />}

          {activeTab === 'webhooks' && canManageWebhooks && <WebhooksManager />}

          {activeTab === 'rulebook' && canManageRulebook && <RulebookBlockBuilder />}

          {activeTab === 'streams' && (canManageStats || canEditStats) && <StreamsManager />}

          {!(canManageStats || canEditStats) && activeTab === 'stats' && (
            <div className="access-denied">
              <p>You don't have permission to access Stats Manager</p>
            </div>
          )}
          {!canEditNews && activeTab === 'news' && (
            <div className="access-denied">
              <p>You don't have permission to access News Manager</p>
            </div>
          )}
          {!canManageUsers && activeTab === 'users' && (
            <div className="access-denied">
              <p>You don't have permission to access User Manager</p>
            </div>
          )}

          {!canManageSeasons && activeTab === 'seasons' && (
            <div className="access-denied">
              <p>You don't have permission to access Season Management</p>
            </div>
          )}

          {!canManageWebhooks && activeTab === 'webhooks' && (
            <div className="access-denied">
              <p>You don't have permission to access Webhooks Manager</p>
            </div>
          )}

          {!canManageRulebook && activeTab === 'rulebook' && (
            <div className="access-denied">
              <p>You don't have permission to access Rulebook Manager</p>
            </div>
          )}
        </div>
      </main>

      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
